import type { Deal } from "../types";
import type { LocalDate, Period } from "./periods";
import {
  isLocalDateWithinPeriod,
  localDateFromDateOnlyString,
  localDateFromInstant,
} from "./periods";

/**
 * Almikia Epic E7 (TASK-090) DOMAIN RULE (a) (ASSUMPTION, until Epic E4's
 * operations entity exists): a closed-won lead counts as a direct sale only
 * for these three lead types -- `colega` (external broker) and a NULL
 * `lead_type` never count, even when the lead is archived.
 */
export const DIRECT_SALE_LEAD_TYPES = ["bpo", "proactivo", "almikia"] as const;

/** DOMAIN RULE (a): a deal counts as a direct sale only at this stage. */
export const CLOSED_WON_STAGE = "venta_cerrada";

export type DirectSale = {
  deal: Deal;
  /** The sale's local calendar date -- `expected_closing_date` when set, else `updated_at` (DOMAIN RULE (a)). */
  localDate: LocalDate;
  /** `deal.amount`, NULL/undefined read as 0 MXN (still counts as 1 unit -- DOMAIN RULE (a)). */
  amountMxn: number;
};

/**
 * True when `deal` is a direct sale per DOMAIN RULE (a): closed-won stage
 * and an eligible `lead_type`. Archived leads still count -- `archived_at`
 * is never checked here.
 */
export function isDirectSale(deal: Deal): boolean {
  return (
    deal.stage === CLOSED_WON_STAGE &&
    !!deal.lead_type &&
    (DIRECT_SALE_LEAD_TYPES as readonly string[]).includes(deal.lead_type)
  );
}

/**
 * The sale's local calendar date, falling back from `expected_closing_date`
 * (a plain date, read as-is) to `updated_at` (a timestamptz instant,
 * resolved to Mexico City local time) -- DOMAIN RULE (a).
 */
export function getSaleLocalDate(deal: Deal): LocalDate {
  return deal.expected_closing_date
    ? localDateFromDateOnlyString(deal.expected_closing_date)
    : localDateFromInstant(deal.updated_at);
}

/**
 * Wraps every direct sale in `deals` with its resolved local date and MXN
 * amount. Order is preserved; `deals` may be scoped to one advisor, a
 * team, or the whole agency by the caller.
 */
export function selectDirectSales(deals: Deal[]): DirectSale[] {
  return deals.filter(isDirectSale).map((deal) => ({
    deal,
    localDate: getSaleLocalDate(deal),
    amountMxn: deal.amount ?? 0,
  }));
}

/**
 * Direct sales in `deals` whose local sale date falls within `period`
 * (DOMAIN RULE (b): calendar periods, never cumulative across them).
 */
export function selectDirectSalesInPeriod(
  deals: Deal[],
  period: Period,
): DirectSale[] {
  return selectDirectSales(deals).filter((sale) =>
    isLocalDateWithinPeriod(sale.localDate, period),
  );
}

/** Sum of `amountMxn` across `sales`. */
export function sumDirectSalesAmountMxn(sales: DirectSale[]): number {
  return sales.reduce((total, sale) => total + sale.amountMxn, 0);
}

/**
 * All-time accumulated direct-sale count for `deals` (already scoped to one
 * advisor) -- DOMAIN RULE (d)'s `min_level` eligibility gate reads this,
 * never a period-scoped count.
 */
export function countAccumulatedDirectSales(deals: Deal[]): number {
  return selectDirectSales(deals).length;
}
